<script setup lang="ts">
import type { EventAttendeeResponse } from '@@/types/api';

const props = defineProps<{
  attendees: EventAttendeeResponse[];
  totalCount: number;
  totalAttendees: number;
  eventId: string;
}>();

function initials(name: string): string {
  return name.charAt(0).toUpperCase();
}

const attendingLine = computed(() => {
  if (props.totalCount === props.totalAttendees) {
    const noun = props.totalAttendees === 1 ? 'person' : 'people';
    return `${props.totalAttendees} ${noun} attending`;
  }
  return `${props.totalCount} commitments · ${props.totalAttendees} people attending`;
});
</script>

<template>
  <div v-if="totalCount > 0" class="mt-4">
    <div class="text-subtitle-2 font-weight-bold mb-2">Attending</div>

    <div class="d-flex align-center mb-2">
      <v-avatar
        v-for="(attendee, index) in attendees"
        :key="attendee.id"
        size="36"
        class="avatar-overlap"
        :style="{
          marginLeft: index > 0 ? '-8px' : '0',
          zIndex: attendees.length - index,
        }"
      >
        <v-img
          v-if="attendee.user.avatar_url"
          :src="attendee.user.avatar_url"
          :alt="attendee.user.name"
        />
        <span
          v-else
          class="bg-primary text-on-primary d-flex align-center justify-center w-100 h-100 text-body-2"
        >
          {{ initials(attendee.user.name) }}
        </span>
      </v-avatar>

      <v-avatar
        v-if="totalCount > attendees.length"
        size="36"
        class="avatar-overlap bg-surface-variant"
        :style="{
          marginLeft: '-8px',
          zIndex: 0,
        }"
      >
        <span class="text-body-2 text-medium-emphasis">
          +{{ totalCount - attendees.length }}
        </span>
      </v-avatar>
    </div>

    <div class="text-body-2 text-medium-emphasis mb-1">
      {{ attendingLine }}
    </div>

    <NuxtLink
      :to="`/events/${eventId}/attendees`"
      class="text-body-2 text-primary text-decoration-none"
    >
      View all
    </NuxtLink>
  </div>
</template>

<style scoped>
.avatar-overlap {
  border: 2px solid rgb(var(--v-theme-surface));
  position: relative;
}
</style>
